<template>
  <MtxPage width="md">
    <template #header>
      <h1>Create Tournament</h1>
    </template>

    <MtxCard v-if="createTxHash" class="text-center">
      <h3>Submitted!</h3>
      <p>
        Your tournament "{{ tournament.title }}" has been successfully submitted and is currently
        waiting to be mined. Depending on network congestion, this can range between seconds and
        several minutes before it shows up on the tournaments page.
      </p>

      <TxProgress class="my-2" :tx="createTxHash" />

      <p v-if="googleCalUrl && icsCalUrl">
        You can add the review period to your calendar:
        <br />
        <a class="btn mx-1 my-1" target="_blank" :href="googleCalUrl">Google Calendar</a>
        <a class="btn mx-1 my-1" target="_blank" :href="icsCalUrl" download="tournament-review.ics">
          Download .ics
        </a>
      </p>
      <RouterLink to="/tournaments" class="mx-2">Go to Tournaments</RouterLink>
      <button @click="resetForm" class="btn--link mx-2">Create Another</button>
    </MtxCard>

    <template v-else>
      <MtxCard v-if="!canCreate" class="text-center mb-2">
        <template v-if="!web3.ready">
          <h5>You must sign in and setup your account to create a Tournament</h5>
          <button @click="requireWeb3()">Sign In</button>
        </template>

        <template v-else-if="!web3.mtxBalance">
          <h5>You need MTX to create a Tournament</h5>
          <ZeroExInstant />
        </template>
      </MtxCard>

      <MtxCard :class="{ 'color-muted': !canCreate }">
        <form @submit.prevent>
          <MtxInput
            v-model="tournament.title"
            :disabled="!canCreate"
            :validation="$v.tournament.title"
            label="Title"
            class="mb-2"
            required
          />

          <MtxInput
            v-if="!canCreate"
            disabled
            label="Description"
            type="textarea"
            class="mb-2"
            required
          />

          <MtxInput
            v-model="tournament.description"
            :validation="$v.tournament.description"
            label="Description"
            type="textarea"
            class="mb-2"
            required
          />

          <MtxUpload
            v-model="tournament.ipfsFiles"
            :disabled="!canCreate"
            label="Supporting Files"
            @loading="isUploading = $event"
            class="mb-2"
          />

          <!-- <MtxInput
            v-model="tournament.url"
            :disabled="!canCreate"
            label="URL"
            type="url"
            class="mb-2"
          /> -->

          <MtxInput
            v-model="tournament.category"
            :disabled="!canCreate"
            label="Category"
            type="select"
            :options="categories"
            class="mb-2"
          />

          <MtxInput
            v-model="tournament.entryFee"
            :disabled="!canCreate"
            :min="$v.tournament.entryFee.$params.min.min"
            label="Entry Fee"
            description="A fee you want contributors to pay before they can make a contribution. This fee is returned to contributors when the tournament ends."
            type="number"
            step="any"
            class="mb-2"
          />

          <MtxInput
            v-model="tournament.bounty"
            :disabled="!canCreate"
            :validation="$v.tournament.bounty"
            :min="$v.tournament.bounty.$params.between.min"
            :max="$v.tournament.bounty.$params.between.max"
            label="Tournament Bounty"
            :description="
              `How much this tournament will be worth. Cannot exceed your MTX balance: ${web3.mtxBalance}`
            "
            type="number"
            step="any"
            class="mb-2"
            required
          />

          <div class="grid column-gap grid-2-columns@sm">
            <MtxInput
              v-model="round.bounty"
              :disabled="!canCreate"
              :validation="$v.round.bounty"
              :min="$v.round.bounty.$params.between.min"
              :max="$v.round.bounty.$params.between.max"
              label="Round Bounty"
              description="This is the amount each round will be worth. It can be changed later on. Cannot exceed the tournament bounty."
              type="number"
              step="any"
              class="mb-2"
              required
            />

            <MtxInput
              v-model="round.review"
              :disabled="!canCreate"
              :validation="$v.round.review"
              label="Review Period"
              description="How much time you would like to review submissions made to each round."
              type="select"
              :options="reviewOptions"
              class="mb-2"
              required
            />
          </div>

          <p>
            Please add your
            <i>ideal</i>
            start and end. Due to the delayed nature of the blockchain, we cannot guarantee the
            exact times, but we can use the start date and calculate the duration based on the end
            date.
          </p>
          <!-- TODO Add link to FAQ about duration -->

          <RoundDatePicker
            ref="roundDatePicker"
            :disabled="!canCreate"
            @dateChange="Object.assign(round, $event)"
            @invalid="dateInvalid = $event"
          />

          <div class="text-center">
            <button
              :class="{ 'btn--disabled': isUploading || isLoading || $v.$invalid }"
              @click="requireWeb3(onSubmit)"
              :disabled="!canCreate"
            >
              {{ !web3.ready ? "Sign in to create" : "Submit" }}
            </button>
          </div>
        </form>
      </MtxCard>
    </template>
  </MtxPage>
</template>

<script>
import { required, maxLength, minValue, between, integer, decimal } from "@/utils/validators"
import RoundDatePicker from "@/components/tournaments/RoundDatePicker"
import ZeroExInstant from "@/components/misc/ZeroExInstant"
import API from "@/api"
import exitGuard from "@/mixins/exitGuard"
import web3 from "@/mixins/web3"
import errorLogger from "@/utils/errorLogger"
import calendar from "@/utils/calendar"

const oneDay = 24 * 60 * 60 * 1000
const tournamentDefaults = {
  title: "",
  description: "",
  ipfsFiles: "",
  // url: "",
  category: "",
  bounty: "",
  entryFee: ""
}
const roundDefaults = {
  bounty: "",
  review: 7 * oneDay,
  start: null,
  duration: null
}

export default {
  mixins: [exitGuard, web3],

  components: {
    RoundDatePicker,
    ZeroExInstant
  },

  data: () => ({
    isUploading: false,
    isLoading: false,
    categories: [
      { value: "", label: "Select a category", disabled: true },
      { value: "art", label: "Art" },
      { value: "environment", label: "Environment" },
      { value: "health", label: "Health" },
      { value: "math", label: "Math" },
      { value: "science", label: "Science" },
      { value: "technology", label: "Technology" }
    ],
    tournament: { ...tournamentDefaults },
    reviewOptions: [
      // { label: "1 hour", value: oneDay / 24 }, // For dev purposes
      { label: "1 day", value: oneDay },
      { label: "3 days", value: 3 * oneDay },
      { label: "7 days", value: 7 * oneDay },
      { label: "15 days", value: 15 * oneDay },
      { label: "30 days", value: 30 * oneDay }
    ],
    round: { ...roundDefaults },
    dateInvalid: true,
    createTxHash: null,
    googleCalUrl: null,
    icsCalUrl: null
  }),

  metaInfo() {
    return {
      titleTemplate: "%s | Create Tournament",
      meta: [{ name: "description", content: "Create a new tournament on Matryx Platform." }]
    }
  },

  computed: {
    canCreate() {
      return this.web3.ready && this.web3.mtxBalance > 0
    },

    preventExit() {
      if (this.createTxHash) return false

      // prevent exit if any tournament fields filled out
      return Object.values(this.tournament).some(x => x)
    }
  },

  methods: {
    async onSubmit() {
      this.$v.$touch()
      this.$refs.roundDatePicker.$v.$touch()
      if (this.$v.$invalid || this.isUploading || this.isLoading) return

      this.isLoading = true

      try {
        const Platform = await this.contracts.Platform
        const upload = API.uploadJson({
          title: this.tournament.title,
          description: this.tournament.description,
          category: this.tournament.category,
          ipfsFiles: this.tournament.ipfsFiles
          // url: this.tournament.url
        })

        this.$web3.$emit("upload", upload)
        const { success, data } = await upload

        if (!success) throw new Error("Unknown error uploading JSON to API")

        // Create the tournament
        const params = [
          {
            content: data.hash,
            bounty: this.tournament.bounty,
            entryFee: this.tournament.entryFee || 0
          },
          {
            bounty: this.round.bounty,
            start: this.round.start,
            // duration: 60 * 3, // For dev purposes
            duration: Math.floor(this.round.duration / 1000),
            review: Math.floor(this.round.review / 1000) // seconds
          }
        ]

        const tx = this.addPlannedTx({
          txFn: () => Platform.createTournament(...params),
          allowance: this.tournament.bounty,
          header: "Create Tournament",
          body: `This transaction will transfer ${this.tournament.bounty} MTX and create your tournament.`
        })

        const { hash } = await tx
        this.createTxHash = hash

        const event = {
          title: "Review period for " + this.tournament.title,
          description: "Matryx Tournament review period.",
          start: new Date(+this.round.start + this.round.duration),
          duration: this.round.review
        }

        this.googleCalUrl = calendar.google(event)
        this.icsCalUrl = calendar.ics(event)

        // this.$router.push({
        //   name: "CreateSubmitted",
        //   params: { createTxHash: hash },
        //   query: {
        //     title: this.tournament.title,
        //     start: +new Date(this.round.end),
        //     duration: params[1].review / 60
        //   }
        // })
        // eslint-disable-next-line no-console
        console.log(
          "The first matrix I designed was quite naturally perfect. It was a work of art. Flawless. Sublime..."
        )
      } catch (error) {
        if (error.message.includes("User denied transaction signature")) return

        errorLogger.log(error)
        this.$notify({
          title: "Error",
          type: "error",
          text: "There was an error creating the tournament. Please try again later."
        })
      } finally {
        this.isLoading = false
      }
    },

    resetForm() {
      this.$v.$reset()
      this.tournament = { ...tournamentDefaults }
      this.round = { ...roundDefaults }
      this.createTxHash = null

      if (this.$refs.roundDatePicker) {
        this.$refs.roundDatePicker.reset()
      }
    }
  },

  validations() {
    return {
      tournament: {
        title: {
          required,
          maxLength: maxLength(255)
        },
        description: {
          required,
          maxLength: maxLength(2000)
        },
        bounty: {
          required,
          between: between(1, this.web3.mtxBalance),
          decimal
        },
        entryFee: {
          min: minValue(0),
          decimal
        }
      },
      round: {
        bounty: {
          required,
          between: between(1, this.tournament.bounty),
          decimal
        },
        review: {
          required,
          between: between(oneDay / 24, 365 * oneDay),
          integer
        },
        validDates: () => !this.dateInvalid
      }
    }
  }
}
</script>
